import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TextEditorStyle } from '../../../../../../../shared/components/text-editor/text-editor.component';

export interface InfoLayout5Data {
  title?: string;
  subtitle?: string; // Large title below images (e.g., "THE GRIME FIGHTERS")
  description?: string;
  buttonText?: string;
  buttonLink?: string;
  image?: {
    src?: string;
    alt?: string;
    width?: string;
    height?: string;
    scaletype?: 'fill' | 'fit' | 'stretch' | 'cover' | 'contain';
  };
  imageUrl?: string;
  backgroundColor?: string;
  titleStyle?: TextEditorStyle;
}

/**
 * Preview component for the "Info Layout 5" content block — renders an image, title and body with configurable styling and emits clicks from action buttons.
 * @component
 * @selector app-info-layout-5-preview
 * @example
 *   <app-info-layout-5-preview
 *     [data]="sampleData"
 *     [headingColor]="'#000'"
 *     [paragraphColor]="'#333'"
 *     [headingFont]="'Arial, sans-serif'"
 *     [bodyFont]="'Roboto, sans-serif'"
 *     [primaryColor]="'#ff0000'"
 *     [titleStyles]="{ 'font-weight': '700' }"
 *     (buttonClick)="handleButtonClick($event)">
 *   </app-info-layout-5-preview>
 * @input {InfoLayout5Data | null} data - Model containing text, image and button/link metadata used to render the layout. May be null when not provided.
 * @input {string | null} headingColor - CSS color value applied to the heading text.
 * @input {string | null} paragraphColor - CSS color value applied to the paragraph/body text.
 * @input {string | null} headingFont - Font-family string applied to heading text.
 * @input {string | null} bodyFont - Font-family string applied to body/paragraph text.
 * @input {string | null} primaryColor - Primary theme color used for accents (e.g., buttons).
 * @input {any} titleStyles - Arbitrary style overrides applied to the title element (object of CSS properties).
 * @output {EventEmitter<{ link: string; event: Event }>} buttonClick - Emitted when a button or link inside the layout is activated; includes the target link and original DOM event.
 *
 * Key methods:
 * - onButtonClick(link: string | undefined, event: Event): void
 *     Emits the buttonClick output only when a valid link is provided. Intended to centralize click handling for action elements.
 * - getImageSrc(): string
 *     Returns the best-available image source from the input data (prefers data.image.src, then data.imageUrl, otherwise empty string).
 * - getImageAlt(): string
 *     Returns the image alt text from the data or a sensible default ("Grime Fighters") if none is provided.
 * - getImageObjectFit(): string
 *     Maps the incoming API image 'scaletype' value to a suitable CSS object-fit value (supports fill/stretch, contain/fit, cover, and defaults to cover).
 *
 * Lifecycle hooks:
 * - This component does not implement Angular lifecycle hooks itself; it reacts to input changes and exposes helper methods for rendering logic and user interactions.
 */
@Component({
  selector: 'app-info-layout-5-preview',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './info-layout-5.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./info-layout-5.component.scss'],
})
export class InfoLayout5PreviewComponent {
  @Input() data: InfoLayout5Data | null = null;
  @Input() headingColor: string | null = null;
  @Input() paragraphColor: string | null = null;
  @Input() headingFont: string | null = null;
  @Input() bodyFont: string | null = null;
  @Input() primaryColor: string | null = null;
  @Input() titleStyles: any = {};
  @Output() buttonClick = new EventEmitter<{ link: string; event: Event }>();

  onButtonClick(link: string | undefined, event: Event): void {
    if (link) {
      this.buttonClick.emit({ link, event });
    }
  }

  getImageSrc(): string {
    return this.data?.image?.src || this.data?.imageUrl || '';
  }

  getImageAlt(): string {
    return this.data?.image?.alt || 'Grime Fighters';
  }

  /**
   * Return the corresponding CSS object-fit value based on the component's image scaletype.
   * @example
   * etImageObjectFit()
   * 'cover'
   * @param {{void}} {{none}} - No parameters; reads scaletype from this.data.image.
   * @returns {{string}} Return the CSS object-fit value ('fill', 'contain', or 'cover').
   **/
  getImageObjectFit(): string {
    const scaletype = this.data?.image?.scaletype || 'cover';
    // Map API scaletype to CSS object-fit
    switch (scaletype) {
      case 'fill':
      case 'stretch':
        return 'fill';
      case 'fit':
        return 'contain';
      case 'cover':
        return 'cover';
      case 'contain':
        return 'contain';
      default:
        return 'cover';
    }
  }
}
